import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter/dist/esm';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { useMetaTags } from '../../shared/hooks/useMetaTags';
import { getSiteUrl } from '../../shared/utils/metaTags';
import { isOwnDomainUrl } from '../../shared/utils/urlUtils';
import { LoadingSpinner } from '../../shared/components/LoadingSpinner';
import { Mermaid } from '../../shared/components/Mermaid';
import {
  isMynazcaDocId,
  loadMynazcaDoc,
  type MynazcaDocId,
} from '../apps/mynazca/loadMynazcaDoc';
import '../../shared/styles/markdown-content.css';
import './MyNazcaDocPage.css';

function MyNazcaDocPage() {
  const lang = useLanguage();
  const navigate = useNavigate();
  const { docSlug } = useParams<{ docSlug: string }>();
  const siteUrl = getSiteUrl();

  const docId: MynazcaDocId | null =
    docSlug != null && isMynazcaDocId(docSlug) ? docSlug : null;

  const [pageTitle, setPageTitle] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const pageUrl =
    docId != null ? `${siteUrl}/${lang}/apps/mynazca/help/${docId}` : `${siteUrl}/${lang}/apps/mynazca`;
  const appTitle = lang === 'ja' ? 'MyNazca(α版)' : 'MyNazca (alpha)';
  const metaTitle = pageTitle ? `${pageTitle} | ${appTitle} | Version0.1` : `${appTitle} | Version0.1`;

  useMetaTags({
    title: metaTitle,
    description: pageTitle || appTitle,
    ogp: {
      title: metaTitle,
      description: pageTitle || appTitle,
      url: pageUrl,
      type: 'website',
      siteName: 'Version0.1',
      image: '/og-image.png',
    },
    twitterCard: {
      card: 'summary' as const,
      title: metaTitle,
      description: pageTitle || appTitle,
      image: '/og-image.png',
    },
    canonicalUrl: pageUrl,
  });

  useEffect(() => {
    if (docId == null) {
      navigate(`/${lang}/not-found-404`, { replace: true });
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    void (async () => {
      try {
        const doc = await loadMynazcaDoc(docId, lang);
        if (!cancelled) {
          setPageTitle(doc.title);
          setMarkdown(doc.body);
        }
      } catch (e) {
        if (!cancelled) {
          setPageTitle('');
          setMarkdown('');
          setLoadError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [docId, lang, navigate]);

  if (docId == null) {
    return null;
  }

  return (
    <main className="mynazca-doc-page">
      <div className="mynazca-doc-page__container">
        <p className="mynazca-doc-page__back">
          <Link to={`/${lang}/apps/mynazca`}>
            {lang === 'ja' ? '← MyNazca(α版) に戻る' : '← Back to MyNazca (alpha)'}
          </Link>
        </p>
        <h1 className="mynazca-doc-page__title">{pageTitle}</h1>
        {loading ? (
          <LoadingSpinner text={lang === 'ja' ? '読み込み中…' : 'Loading…'} />
        ) : loadError != null ? (
          <p className="mynazca-doc-page__error" role="alert">
            {loadError}
          </p>
        ) : (
          <div className="mynazca-doc-page__markdown markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={{
                a: ({ href, children, ...props }) => {
                  const isOwnDomain = href != null && isOwnDomainUrl(href);
                  return (
                    <a
                      href={href}
                      {...props}
                      {...(isOwnDomain ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                    >
                      {children}
                    </a>
                  );
                },
                img: ({ src, alt, ...props }) => (
                  <img src={src} alt={alt ?? ''} loading="lazy" {...props} />
                ),
                code({ className, children, ...props }) {
                  const match = /language-([^:]+)/.exec(className || '');
                  const language = match ? match[1] : '';
                  const codeString = String(children).replace(/\n$/, '');
                  const isInline = !className || !className.includes('language-');

                  if (isInline) {
                    return (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  }

                  if (language === 'mermaid') {
                    return <Mermaid chart={codeString} />;
                  }

                  return (
                    <SyntaxHighlighter
                      language={language || 'text'}
                      style={vscDarkPlus}
                      PreTag="div"
                      {...props}
                    >
                      {codeString}
                    </SyntaxHighlighter>
                  );
                },
              }}
            >
              {markdown}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </main>
  );
}

export default MyNazcaDocPage;
